import type { CreatureState } from "@eldrun/content";
import type { CreatureRig } from "./creature-model.ts";

/** O que a animação lê de cada criatura (interpolada) e do tempo no estado. */
export interface CreaturePoseInput {
  state: CreatureState;
  /** Velocidade horizontal estimada (m/s). */
  speed: number;
  /** Segundos desde que entrou no estado. */
  stateTime: number;
  /** Voadora no ar (bate as asas). */
  flying?: boolean;
}

export interface CreaturePose {
  legSwing: number;
  bodyY: number;
  bodyPitch: number;
  bodyRoll: number;
  headPitch: number;
  /** Pernas visíveis (o tatu enrolado as recolhe). */
  legsVisible: boolean;
  /** Escala vertical do corpo (enrolar). */
  squash: number;
  /** Uma das pernas chutando (ema). */
  kick: number;
  /** Braços: balanço da caminhada e quanto estão erguidos (negativo = para cima, à frente). */
  armSwing: number;
  armRaise: number;
  /** Batida das asas (ângulo, rad). */
  wingFlap: number;
  /** Escala geral (a assombração encolhe ao se desfazer). */
  scale: number;
}

/**
 * Pose procedural (sem clipes, ADR 0007): passada pela velocidade; surgir sobe do chão; enrolar
 * achata e esconde as pernas; cambalear balança; preparar inclina para trás, recolhe a perna e
 * ergue os braços; golpear chuta ou desce os braços e avança o corpo; vulnerável arqueia e
 * balança; cavar afunda; morta tomba e afunda no chão; dissolver encolhe e afunda. Os materiais
 * toon são compartilhados entre criaturas, então sumir por opacidade exigiria um material por corpo.
 */
export class CreatureAnimator {
  /** Fase da passada (rad): a troca com o modelo animado (F50) a copia nos dois sentidos. */
  phase = 0;
  private flap = 0;

  pose(input: CreaturePoseInput, dt: number, legLength: number): CreaturePose {
    const stride = Math.min(1.4, input.speed / Math.max(0.5, legLength * 6));
    this.phase += dt * (3 + input.speed * 2.2);
    this.flap += dt * (input.flying ? 9 : 0);
    const swing = Math.sin(this.phase) * 0.6 * stride;
    const base: CreaturePose = {
      legSwing: swing,
      bodyY: Math.abs(Math.cos(this.phase)) * 0.03 * stride,
      bodyPitch: 0,
      bodyRoll: 0,
      headPitch: 0,
      legsVisible: true,
      squash: 1,
      kick: 0,
      armSwing: swing * 0.8,
      armRaise: 0,
      wingFlap: input.flying ? Math.sin(this.flap) * 0.9 : 0.15,
      scale: 1,
    };
    const t = input.stateTime;
    switch (input.state) {
      case "surgindo":
        return { ...base, legSwing: 0, bodyY: -0.6 * Math.max(0, 1 - t / 1.2), headPitch: 0.4 };
      case "alerta":
        return { ...base, headPitch: -0.3 };
      case "perseguindo":
        return { ...base, bodyPitch: Math.min(0.25, input.speed * 0.04), headPitch: -0.1 };
      case "enrolado":
        return { ...base, legSwing: 0, legsVisible: false, squash: 0.75, bodyRoll: Math.sin(t * 9) * 0.05 };
      case "cambaleando":
        return { ...base, legSwing: 0, bodyRoll: Math.sin(t * 22) * 0.3, headPitch: 0.3, armRaise: 0.4 };
      case "preparando": {
        const k = Math.min(1, t / 0.4);
        return { ...base, legSwing: 0, bodyPitch: -0.35 * k, kick: -0.6 * k, armSwing: 0, armRaise: -2.2 * k };
      }
      case "golpeando":
        return { ...base, bodyPitch: 0.3, kick: 1.3, armSwing: 0, armRaise: 0.6 };
      case "recuperando":
        return { ...base, kick: 0.4 * Math.max(0, 1 - t / 0.5), armRaise: 0.3 * Math.max(0, 1 - t / 0.5) };
      case "vulneravel":
        return { ...base, legSwing: 0, bodyPitch: 0.45, bodyY: -0.08, headPitch: 0.5, bodyRoll: Math.sin(t * 6) * 0.08, armSwing: 0, armRaise: 0.5, wingFlap: 0.6 };
      case "cavando":
        return { ...base, bodyY: -0.5 * Math.min(1, t / 1.2), bodyPitch: 0.5, legSwing: Math.sin(t * 25) * 0.6 };
      case "dissolvendo": {
        const k = Math.min(1, t / 1.2);
        return { ...base, legSwing: 0, armSwing: 0, bodyY: -0.3 * k, headPitch: 0.4, scale: Math.max(0.01, 1 - k) };
      }
      case "sumindo":
        // Some em fumaça (Cuca Rainha) e reaparece longe, já inteira.
        return { ...base, legSwing: 0, armSwing: 0, armRaise: 1.2, scale: Math.max(0.01, 1 - t / 0.4) };
      case "morta":
        return { ...base, legSwing: 0, armSwing: 0, wingFlap: 0, bodyRoll: Math.min(1.5, t * 4), bodyY: -0.05 - Math.min(0.6, Math.max(0, t - 0.9)) };
      default:
        return base;
    }
  }
}

export function applyCreaturePose(rig: CreatureRig, pose: CreaturePose): void {
  rig.body.position.y = pose.bodyY;
  rig.body.rotation.set(pose.bodyPitch, 0, pose.bodyRoll);
  rig.body.scale.set(pose.scale, pose.squash * pose.scale, pose.scale);
  rig.head.rotation.x = pose.headPitch;
  rig.legs.forEach((leg, i) => {
    leg.visible = pose.legsVisible;
    // Pernas alternadas em diagonal (quadrúpedes) ou em oposição (bípedes).
    const sign = rig.legs.length === 4 ? (i === 0 || i === 3 ? 1 : -1) : i === 0 ? 1 : -1;
    leg.rotation.x = pose.legSwing * sign + (i === 1 ? pose.kick : 0);
  });
  rig.arms.forEach((arm, i) => {
    arm.rotation.x = pose.armSwing * (i === 0 ? -1 : 1) + pose.armRaise;
  });
  rig.wings.forEach((wing, i) => {
    wing.rotation.z = pose.wingFlap * (i === 0 ? 1 : -1);
  });
}
